import { useState, useEffect, useRef } from 'react';
import { useAuthStore } from '@/shared/stores/authStore';
import { toast } from "react-toastify";
import {
    User,
    Camera,
    Calendar,
    MapPin,
    CreditCard,
    Shield,
    Upload,
    Check,
    AlertCircle,
    Save,
    Building2,
    Hash,
    Wallet,
    FileText,
    Phone,
    Lock,
    Eye,
    EyeOff
} from 'lucide-react';
import Header from '@/react-app/components/Header';
import { api } from '@/shared/api/endpoints';
import { useTheme } from '@/react-app/contexts/ThemeContext';
import { useNavigate } from "react-router-dom";
import { mapToProfileData } from "@/shared/utils/transformers";
import type { ProfileData } from '@/shared/types';
import heic2any from "heic2any";


export default function Settings() {
    const { theme } = useTheme();
    const isDark = theme === 'dark';
    const fileInputRef = useRef<HTMLInputElement>(null);
    const kycFileInputRef = useRef<HTMLInputElement>(null);
    const navigate = useNavigate();

    const [activeTab, setActiveTab] = useState(0);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
    const { user: authStoreUser } = useAuthStore();

    // Password update states
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showCurrentPassword, setShowCurrentPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    // File preview states
    const [profileFilePreview, setProfileFilePreview] = useState<{ file: File; url: string } | null>(null);
    const [kycFilePreview, setKycFilePreview] = useState<{ file: File; url: string } | null>(null);

    const [profileData, setProfileData] = useState<ProfileData>({
        _id: '',
        email: '',
        firstName: '',
        lastName: '',
        phone: '',
        dob: '',
        address: '',
        profilePictureUrl: '',
        bankName: '',
        bankAccountNumber: '',
        bankAccountName: '',
        wallet_address: '',
        network: '',
        kycDocumentType: '',
        kycDocumentUrl: '',
        kycVerificationStatus: '',
        kycSubmittedAt: '',
        kycVerifiedAt: '',
        payMethod: '',
        token: '',
        updatePasswordDate: '',
        createdAt: ''
    });

    const tabs = [
        {
            id: 0,
            label: 'Personal Information',
            icon: User,
            description: 'Update your personal details and profile picture'
        },
        {
            id: 1,
            label: 'Payment Details',
            icon: CreditCard,
            description: 'Manage your withdrawal and payment information'
        },
        {
            id: 2,
            label: 'Security',
            icon: Shield,
            description: 'Update your password and security settings'
        },
        {
            id: 3,
            label: 'KYC Documents',
            icon: Shield,
            description: 'Upload and manage your verification documents'
        }
    ];

    const cryptoNetworks = [
        'Tron', 'Binance Smart Chain'
    ];

    const paymentMethods = [
        'fiat', 'crypto', 'both'
    ];

    const documentTypes = [
        { value: 'Select Document', label: 'Select Document' },
        { value: 'INTL_PASSPORT', label: 'International Passport' },
        { value: 'VOTERS_CARD', label: 'Voters Card' },
        { value: 'NIN', label: 'National Identification Number (NIN)' }
    ];

    useEffect(() => {
        if (authStoreUser) {
            fetchProfileData();
        }
    }, [authStoreUser]);

    const fetchProfileData = async () => {
        try {
            const response = await api.user.getProfile();
            if (response.success && response.data) {
                setProfileData(mapToProfileData(response.data));
            }
        } catch (error) {
            console.error('Error fetching profile data:', error);
        } finally {
            setLoading(false);
        }
    };


    // Cleanup effect for file previews
    useEffect(() => {
        return () => {
            if (profileFilePreview) {
                URL.revokeObjectURL(profileFilePreview.url);
            }
            if (kycFilePreview) {
                URL.revokeObjectURL(kycFilePreview.url);
            }
        };
    }, [profileFilePreview, kycFilePreview]);



    const handleInputChange = (field: keyof ProfileData, value: string) => {
        setProfileData(prev => ({ ...prev, [field]: value }));
        setMessage(null);
    };

    const validatePassword = (password: string) => {
        const requirements = {
            length: password.length >= 8,
            uppercase: /[A-Z]/.test(password),
            lowercase: /[a-z]/.test(password),
            number: /\d/.test(password),
            special: /[!@#$%^&*(),.?":{}|<>]/.test(password)
        };

        return requirements;
    };

    // const handleFileSelect = (file: File, type: 'profile' | 'kyc') => {
    //     const url = URL.createObjectURL(file);

    //     if (type === 'profile') {
    //         // Clear previous preview URL to prevent memory leaks
    //         if (profileFilePreview) {
    //             URL.revokeObjectURL(profileFilePreview.url);
    //         }
    //         setProfileFilePreview({ file, url });
    //     } else {
    //         // Clear previous preview URL to prevent memory leaks
    //         if (kycFilePreview) {
    //             URL.revokeObjectURL(kycFilePreview.url);
    //         }
    //         setKycFilePreview({ file, url });
    //     }
    //     setMessage(null);
    // };


    const handleFileSelect = async (file: File, type: 'profile' | 'kyc') => {
        let processedFile = file;

        // 1. Detect HEIC/HEIF files (Common on iOS)
        const isHeic = file.type === "image/heic" ||
            file.type === "image/heif" ||
            file.name.toLowerCase().endsWith(".heic") ||
            file.name.toLowerCase().endsWith(".heif");

        if (isHeic) {
            try {
                // 2. Convert HEIC to JPEG
                const convertedBlob = await heic2any({
                    blob: file,
                    toType: "image/jpeg",
                    quality: 0.8 // Adjust quality as needed
                });

                // Handle the result (heic2any can return an array if the HEIC has multiple frames)
                const blob = Array.isArray(convertedBlob) ? convertedBlob[0] : convertedBlob;

                // 3. Create a new File object from the Blob
                processedFile = new File([blob], file.name.replace(/\.(heic|heif)$/i, ".jpg"), {
                    type: "image/jpeg",
                    lastModified: Date.now()
                });
            } catch (error) {
                console.error("HEIC conversion failed:", error);
                // Fallback: Continue with original file if conversion fails
            }
        }

        // 4. Create Preview URL for the (potentially converted) file
        const url = URL.createObjectURL(processedFile);

        if (type === 'profile') {
            if (profileFilePreview) {
                URL.revokeObjectURL(profileFilePreview.url);
            }
            setProfileFilePreview({ file: processedFile, url });
        } else {
            if (kycFilePreview) {
                URL.revokeObjectURL(kycFilePreview.url);
            }
            setKycFilePreview({ file: processedFile, url });
        }

        setMessage(null);
    };

    const clearFilePreview = (type: 'profile' | 'kyc') => {
        if (type === 'profile' && profileFilePreview) {
            URL.revokeObjectURL(profileFilePreview.url);
            setProfileFilePreview(null);
        } else if (type === 'kyc' && kycFilePreview) {
            URL.revokeObjectURL(kycFilePreview.url);
            setKycFilePreview(null);
        }
    };


    const handlePasswordUpdate = async () => {
        setSaving(true);
        setMessage(null);

        // Validate new password
        const passwordRequirements = validatePassword(newPassword);
        const isPasswordValid = Object.values(passwordRequirements).every(Boolean);

        if (!isPasswordValid) {
            setMessage({ type: 'error', text: 'New password does not meet all requirements' });
            toast.error("New password does not meet all requirements");
            setSaving(false);
            return;
        }

        if (newPassword !== confirmPassword) {
            setMessage({ type: 'error', text: 'New passwords do not match' });
            toast.error("New passwords do not match");
            setSaving(false);
            return;
        }

        try {
            const payload = {
                "oldPassword": currentPassword,
                "newPassword": newPassword
            }
            const response = await api.auth.updatePassword(payload);

            if (response.success) {
                setMessage({ type: 'success', text: 'Password updated successfully!' });
                toast.success("Password updated successfully! 🎉");
                setCurrentPassword('');
                setNewPassword('');
                setConfirmPassword('');
            } else {
                const errorData = await response?.error;
                toast.error(errorData || "Failed to update password");
                setMessage({ type: 'error', text: errorData || 'Failed to update password' });
            }
        } catch (error) {
            setMessage({ type: 'error', text: 'An unexpected error occurred' });
        } finally {
            setSaving(false);
        }
    };



    const handleSave = async (tabIndex: number) => {
        setSaving(true);
        setMessage(null);

        try {
            const formData = new FormData();

            if (tabIndex === 0) {
                formData.append('firstName', profileData.firstName || '');
                formData.append('lastName', profileData.lastName || '');
                formData.append('phone', profileData.phone || '');
                formData.append('dob', profileData.dob || '');
                formData.append('address', profileData.address || '');
                formData.append('tabIndex', '0');

                // Add profile picture file if selected
                if (profileFilePreview) {
                    formData.append('profileImage', profileFilePreview.file);
                    // Clear the preview after adding to form
                    clearFilePreview('profile');
                }

                const result = await api.user.updateProfile(formData);

                if (result.success) {
                    // setProfileData({ ...profileData, ...result.data });
                    toast.success("Settings updated successfully! 🎉");
                    setMessage({ type: 'success', text: 'Settings updated successfully!' });
                    clearFilePreview('profile');
                } else {
                    setMessage({ type: 'error', text: result.error || 'Failed to update settings' });
                    toast.error("Failed to update settings");
                }

            } else if (tabIndex === 1) {

                formData.append('tabIndex', '1');

                const payData = {
                    "bank_name": profileData.bankName || '',
                    "account_number": profileData.bankAccountNumber || '',
                    "account_name": profileData.bankAccountName || '',
                    "wallet_address": profileData.wallet_address || '',
                    "network": profileData.network || '',
                    "pay_type": profileData.payMethod || ''
                }

                const result = await api.user.updatePaymentDetails(payData);

                if (result.success) {
                    // setProfileData({ ...profileData, ...result.data });
                    toast.success("Payment details updated successfully! 🎉");
                    setMessage({ type: 'success', text: 'Payment details updated successfully!' });
                } else {
                    setMessage({ type: 'error', text: result.error || 'Failed to update payment details' });
                    toast.error("Failed to update payment details");
                }


            } else if (tabIndex === 2) {
                // Security tab - handle password update
                await handlePasswordUpdate();
                return;
            } else if (tabIndex === 3) {
                // KYC Documents
                if (profileData.kycDocumentType == "") {
                    toast.error("Select a valid document type");
                    return;
                }
                formData.append('idType', profileData.kycDocumentType || '');
                // formData.append('tabIndex', '3');

                // Add KYC document file if selected
                if (kycFilePreview) {
                    formData.append('kycDocument', kycFilePreview.file);
                    clearFilePreview('kyc');
                }

                for (let pair of formData.entries()) {
                    console.log(pair[0], pair[1]);
                }

                const result = await api.user.updateKyc(formData);

                if (result.success) {
                    // setProfileData({ ...profileData, ...result.data });
                    toast.success("Kyc details submitted successfully! 🎉");
                    setMessage({ type: 'success', text: 'Kyc details submitted successfully!' });
                    clearFilePreview('profile');
                } else {
                    setMessage({ type: 'error', text: result.error || 'Failed to submit kyc details' });
                    toast.error("Failed to update kyc details");
                }
            }



            setSaving(false);
            fetchProfileData();


        } catch (error: any) {
            console.error('Settings update error:', error);
            setMessage({ type: 'error', text: 'An unexpected error occurred' });
            setSaving(false);
        } finally {
            setSaving(false);
        }
    };

    // const getVerificationStatusBadge = (status: string) => {
    //     const statusConfig = {
    //         Pending_Review: { color: 'yellow', text: 'Pending_Review' },
    //         Verified: { color: 'green', text: 'Verified' },
    //         Rejected: { color: 'red', text: 'Rejected' },
    //     };

    //     const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.Pending_Review;
    //     console.log(profileData.kycVerificationStatus);

    //     return (
    //         <div className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${config.color === 'green'
    //             ? 'bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-300'
    //             : config.color === 'yellow'
    //                 ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/50 dark:text-yellow-300'
    //                 : 'bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-300'
    //             }`}>
    //             {config.color === 'green' && <Check className="h-3 w-3 mr-1" />}
    //             {config.color === 'yellow' && <AlertCircle className="h-3 w-3 mr-1" />}
    //             {config.color === 'red' && <AlertCircle className="h-3 w-3 mr-1" />}
    //             {config.text}
    //         </div>
    //     );
    // };

    const getVerificationStatusBadge = (status: string) => {
        let config: {
            color: "red" | "yellow" | "green";
            text: string;
            icon: any;
        };

        if (status === "Verified") {
            config = {
                color: "green",
                text: "Fully Verified – Full Access",
                icon: Check,
            };
        }
        // Unverified states
        else if (
            status === "Not_Submitted" ||
            status === "Pending_Review" ||
            status === "Rejected"
        ) {
            config = {
                color: "red",
                text: "Unverified – Cannot Invest",
                icon: AlertCircle,
            };
        }
        // Future / Partial verification
        else {
            config = {
                color: "red",
                text: "Not Submitted – Cannot Invest",
                icon: AlertCircle,
            };
        }

        const IconComponent = config.icon;

        return (
            <div
                className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${config.color === "green"
                    ? "bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-300"
                    : config.color === "yellow"
                        ? "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/50 dark:text-yellow-300"
                        : "bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-300"
                    }`}
            >
                <IconComponent className="h-3 w-3 mr-1" />
                {config.text}<br />
                {status}
            </div>
        );
    };

    if (loading) {
        return (
            <div className={`min-h-screen ${isDark ? 'bg-gray-900' : 'bg-gray-50'}`}>
                <Header variant="dashboard" />
                <div className="flex items-center justify-center min-h-[calc(100vh-4rem)]">
                    <div className="text-center">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                        <p className={isDark ? 'text-gray-300' : 'text-gray-600'}>Loading settings...</p>
                    </div>
                </div>
            </div>
        );
    }

    if (!authStoreUser) {
        console.log(authStoreUser);
        navigate("/")
        return null;
    }

    return (
        <div className={`min-h-screen ${isDark ? 'bg-gray-900' : 'bg-gray-50'}`}>
            <Header variant="dashboard" />

            <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Header */}
                <div className="mb-8">
                    <h1 className={`text-3xl font-bold ${isDark ? 'text-white' : 'text-gray-900'} mb-2`}>
                        Account Settings
                    </h1>
                    <p className={`${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                        Manage your account information and preferences
                    </p>
                </div>

                {/* Message */}
                {message && (
                    <div className={`mb-6 p-4 rounded-xl ${message.type === 'success'
                        ? 'bg-green-100 border border-green-200 text-green-800 dark:bg-green-900/50 dark:border-green-800 dark:text-green-300'
                        : 'bg-red-100 border border-red-200 text-red-800 dark:bg-red-900/50 dark:border-red-800 dark:text-red-300'
                        }`}>
                        <div className="flex items-center">
                            {message.type === 'success' ? <Check className="h-5 w-5 mr-2" /> : <AlertCircle className="h-5 w-5 mr-2" />}
                            {message.text}
                        </div>
                    </div>
                )}

                {/* Tabs */}
                <div className={`${isDark ? 'bg-gray-800' : 'bg-white'} rounded-3xl shadow-xl overflow-hidden`}>
                    {/* Tab Navigation */}
                    <div className={`border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                        <nav className="flex space-x-8 px-8 pt-6 overflow-x-auto no-scrollbar scroll-smooth">
                            {tabs.map((tab) => (
                                <button
                                    key={tab.id}
                                    onClick={() => setActiveTab(tab.id)}
                                    className={`flex items-center space-x-2 pb-4 border-b-2 transition-colors whitespace-nowrap ${activeTab === tab.id
                                        ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                                        : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
                                        }`}
                                >
                                    <tab.icon className="h-5 w-5 flex-shrink-0" />
                                    <span className="font-medium">{tab.label}</span>
                                </button>
                            ))}
                        </nav>
                    </div>

                    {/* Tab Content */}
                    <div className="p-8">
                        {/* Tab 0: Personal Information */}
                        {activeTab === 0 && (
                            <div className="space-y-8">
                                <div>
                                    <h3 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'} mb-2`}>
                                        Personal Information
                                    </h3>
                                    <p className={`${isDark ? 'text-gray-300' : 'text-gray-600'} mb-6`}>
                                        Update your personal details and profile picture
                                    </p>
                                </div>

                                {/* Profile Picture Upload */}
                                <div className="flex items-center space-x-6">
                                    <div className="relative">
                                        {profileFilePreview ? (
                                            <img
                                                src={profileFilePreview.url}
                                                alt="Profile Preview"
                                                className="w-24 h-24 rounded-full object-cover border-4 border-blue-500 shadow-lg"
                                            />
                                        ) : profileData.profilePictureUrl ? (
                                            <img
                                                src={profileData.profilePictureUrl}
                                                alt="Profile"
                                                className="w-24 h-24 rounded-full object-cover border-4 border-white shadow-lg"
                                            />
                                        ) : (
                                            <div className="w-24 h-24 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center shadow-lg">
                                                <User className="h-12 w-12 text-white" />
                                            </div>
                                        )}
                                        <button
                                            onClick={() => fileInputRef.current?.click()}
                                            className="absolute bottom-0 right-0 w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center text-white shadow-lg hover:bg-blue-700 transition-colors"
                                        >
                                            <Camera className="h-4 w-4" />
                                        </button>
                                    </div>
                                    <div>
                                        <h4 className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'} mb-1`}>
                                            Profile Picture
                                        </h4>
                                        <p className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'} mb-2`}>
                                            {profileFilePreview ? 'Preview - save to upload' : 'Upload a clear photo of yourself'}
                                        </p>
                                        <div className="flex items-center space-x-2">
                                            <button
                                                onClick={() => fileInputRef.current?.click()}
                                                className="text-blue-600 hover:text-blue-700 text-sm font-medium"
                                            >
                                                {profileFilePreview ? 'Change picture' : 'Upload new picture'}
                                            </button>
                                            {profileFilePreview && (
                                                <button
                                                    className="text-red-600 hover:text-red-700 text-sm font-medium"
                                                    onClick={() => {
                                                        clearFilePreview('profile');
                                                        if (fileInputRef.current) fileInputRef.current.value = "";
                                                    }}
                                                >
                                                    Remove
                                                </button>
                                            )}
                                        </div>
                                        {profileFilePreview && (
                                            <p className={`text-xs ${isDark ? 'text-blue-400' : 'text-blue-600'} mt-1`}>
                                                File selected: {profileFilePreview.file.name}
                                            </p>
                                        )}
                                    </div>
                                    {/* <input
                                        ref={fileInputRef}
                                        type="file"
                                        accept="image/*"
                                        className="hidden"
                                        onChange={(e) => {
                                            const file = e.target.files?.[0];
                                            if (file) handleFileSelect(file, 'profile');
                                        }}
                                    /> */}
                                    <input
                                        ref={fileInputRef}
                                        type="file"
                                        className="sr-only"
                                        accept="image/jpeg,image/png,image/heic,image/heif"
                                        onChange={(e) => {
                                            const file = e.target.files?.[0];
                                            if (file) handleFileSelect(file, 'profile');
                                        }}
                                    />
                                </div>

                                {/* Personal Details Form */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div>
                                        <label className={`block text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'} mb-2`}>
                                            First Name
                                        </label>
                                        <div className="relative">
                                            <User className={`absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 ${isDark ? 'text-gray-400' : 'text-gray-400'}`} />
                                            <input
                                                type="text"
                                                value={profileData.firstName}
                                                onChange={(e) => handleInputChange('firstName', e.target.value)}
                                                className={`w-full pl-10 pr-4 py-3 border rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors ${isDark
                                                    ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                                                    : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                                                    }`}
                                                placeholder="Enter your first name"
                                            />
                                        </div>
                                    </div>

                                    <div>
                                        <label className={`block text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'} mb-2`}>
                                            Last Name
                                        </label>
                                        <div className="relative">
                                            <User className={`absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 ${isDark ? 'text-gray-400' : 'text-gray-400'}`} />
                                            <input
                                                type="text"
                                                value={profileData.lastName}
                                                onChange={(e) => handleInputChange('lastName', e.target.value)}
                                                className={`w-full pl-10 pr-4 py-3 border rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors ${isDark
                                                    ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                                                    : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                                                    }`}
                                                placeholder="Enter your last name"
                                            />
                                        </div>
                                    </div>

                                    <div>
                                        <label className={`block text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'} mb-2`}>
                                            Phone Number
                                        </label>
                                        <div className="relative">
                                            <Phone className={`absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 ${isDark ? 'text-gray-400' : 'text-gray-400'}`} />
                                            <input
                                                type="tel"
                                                value={profileData.phone}
                                                onChange={(e) => handleInputChange('phone', e.target.value)}
                                                className={`w-full pl-10 pr-4 py-3 border rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors ${isDark
                                                    ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                                                    : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                                                    }`}
                                                placeholder="Enter your phone number"
                                            />
                                        </div>
                                    </div>

                                    <div>
                                        <label className={`block text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'} mb-2`}>
                                            Date of Birth
                                        </label>
                                        <div className="relative">
                                            <Calendar className={`absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 ${isDark ? 'text-gray-400' : 'text-gray-400'} pointer-events-none z-10`} />
                                            <input
                                                type="date"
                                                value={profileData.dob}
                                                onChange={(e) => handleInputChange('dob', e.target.value)}
                                                className={`w-full pl-10 pr-4 py-3 border rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors relative z-20 ${isDark
                                                    ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                                                    : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                                                    }`}
                                                style={{
                                                    colorScheme: isDark ? 'dark' : 'light'
                                                }}
                                            />
                                        </div>
                                    </div>

                                    <div className="md:col-span-2">
                                        <label className={`block text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'} mb-2`}>
                                            Address
                                        </label>
                                        <div className="relative">
                                            <MapPin className={`absolute left-3 top-3 h-5 w-5 ${isDark ? 'text-gray-400' : 'text-gray-400'}`} />
                                            <textarea
                                                value={profileData.address}
                                                onChange={(e) => handleInputChange('address', e.target.value)}
                                                rows={3}
                                                className={`w-full pl-10 pr-4 py-3 border rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors resize-none ${isDark
                                                    ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                                                    : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                                                    }`}
                                                placeholder="Enter your full address"
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div className="flex justify-end">
                                    <button
                                        onClick={() => handleSave(0)}
                                        disabled={saving}
                                        className="bg-blue-600 text-white px-8 py-3 rounded-xl font-semibold hover:bg-blue-700 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                                    >
                                        <Save className="h-5 w-5" />
                                        <span>{saving ? 'Saving...' : 'Save Changes'}</span>
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* Tab 1: Payment Details */}
                        {activeTab === 1 && (
                            <div className="space-y-8">
                                <div>
                                    <h3 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'} mb-2`}>
                                        Payment & Withdrawal Details
                                    </h3>
                                    <p className={`${isDark ? 'text-gray-300' : 'text-gray-600'} mb-6`}>
                                        Manage your bank account and crypto wallet information for withdrawals
                                    </p>
                                </div>

                                {/* Bank Account Details */}
                                <div className={`${isDark ? 'bg-gray-700' : 'bg-gray-50'} rounded-2xl p-6`}>
                                    <h4 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'} mb-4 flex items-center`}>
                                        <Building2 className="h-5 w-5 mr-2 text-blue-600" />
                                        Bank Account Details
                                    </h4>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        <div>
                                            <label className={`block text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'} mb-2`}>
                                                Bank Name
                                            </label>
                                            <input
                                                type="text"
                                                value={profileData.bankName}
                                                onChange={(e) => handleInputChange('bankName', e.target.value)}
                                                className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors ${isDark
                                                    ? 'bg-gray-600 border-gray-500 text-white placeholder-gray-400'
                                                    : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                                                    }`}
                                                placeholder="Enter your bank name"
                                            />
                                        </div>

                                        <div>
                                            <label className={`block text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'} mb-2`}>
                                                Account Number
                                            </label>
                                            <div className="relative">
                                                <Hash className={`absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 ${isDark ? 'text-gray-400' : 'text-gray-400'}`} />
                                                <input
                                                    type="text"
                                                    value={profileData.bankAccountNumber}
                                                    onChange={(e) => handleInputChange('bankAccountNumber', e.target.value)}
                                                    className={`w-full pl-10 pr-4 py-3 border rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors ${isDark
                                                        ? 'bg-gray-600 border-gray-500 text-white placeholder-gray-400'
                                                        : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                                                        }`}
                                                    placeholder="Enter your account number"
                                                />
                                            </div>
                                        </div>

                                        <div>
                                            <label className={`block text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'} mb-2`}>
                                                Account Name
                                            </label>
                                            <input
                                                type="text"
                                                value={profileData.bankAccountName}
                                                onChange={(e) => handleInputChange('bankAccountName', e.target.value)}
                                                className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors ${isDark
                                                    ? 'bg-gray-600 border-gray-500 text-white placeholder-gray-400'
                                                    : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                                                    }`}
                                                placeholder="Enter the account holder name"
                                            />
                                        </div>
                                        <div>

                                            <label className={`block text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'} mb-2`}>
                                                Payment Type
                                            </label>
                                            <select
                                                value={profileData.payMethod}
                                                onChange={(e) => handleInputChange('payMethod', e.target.value)}
                                                className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors ${isDark
                                                    ? 'bg-gray-600 border-gray-500 text-white'
                                                    : 'bg-white border-gray-300 text-gray-900'
                                                    }`}
                                            >
                                                {paymentMethods.map((payMethod) => (
                                                    <option key={payMethod} value={payMethod}>{payMethod}</option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>
                                </div>

                                {/* Crypto Wallet Details */}
                                <div className={`${isDark ? 'bg-gray-700' : 'bg-gray-50'} rounded-2xl p-6`}>
                                    <h4 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'} mb-4 flex items-center`}>
                                        <Wallet className="h-5 w-5 mr-2 text-purple-600" />
                                        Crypto Wallet Details
                                    </h4>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        <div>
                                            <label className={`block text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'} mb-2`}>
                                                Crypto Network
                                            </label>
                                            <select
                                                value={profileData.network}
                                                onChange={(e) => handleInputChange('network', e.target.value)}
                                                className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors ${isDark
                                                    ? 'bg-gray-600 border-gray-500 text-white'
                                                    : 'bg-white border-gray-300 text-gray-900'
                                                    }`}
                                            >
                                                {cryptoNetworks.map((network) => (
                                                    <option key={network} value={network}>{network}</option>
                                                ))}
                                            </select>
                                        </div>

                                        <div className="md:col-span-1">
                                            <label className={`block text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'} mb-2`}>
                                                Wallet Address
                                            </label>
                                            <input
                                                type="text"
                                                value={profileData.wallet_address}
                                                onChange={(e) => handleInputChange('wallet_address', e.target.value)}
                                                className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors font-mono text-sm ${isDark
                                                    ? 'bg-gray-600 border-gray-500 text-white placeholder-gray-400'
                                                    : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                                                    }`}
                                                placeholder="Enter your wallet address"
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div className="flex justify-end">
                                    <button
                                        onClick={() => handleSave(1)}
                                        disabled={saving}
                                        className="bg-blue-600 text-white px-8 py-3 rounded-xl font-semibold hover:bg-blue-700 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                                    >
                                        <Save className="h-5 w-5" />
                                        <span>{saving ? 'Saving...' : 'Save Changes'}</span>
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* Tab 2: Security */}
                        {activeTab === 2 && (
                            <div className="space-y-8">
                                <div>
                                    <h3 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'} mb-2`}>
                                        Security Settings
                                    </h3>
                                    <p className={`${isDark ? 'text-gray-300' : 'text-gray-600'} mb-6`}>
                                        Update your password to keep your account secure
                                    </p>
                                </div>

                                {/* Password Update Form */}
                                <div className={`${isDark ? 'bg-gray-700' : 'bg-gray-50'} rounded-2xl p-6`}>
                                    <h4 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'} mb-4 flex items-center`}>
                                        <Lock className="h-5 w-5 mr-2 text-blue-600" />
                                        Change Password
                                    </h4>

                                    <div className="space-y-6">
                                        <div>
                                            <label className={`block text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'} mb-2`}>
                                                Current Password
                                            </label>
                                            <div className="relative">
                                                <Lock className={`absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 ${isDark ? 'text-gray-400' : 'text-gray-400'}`} />
                                                <input
                                                    type={showCurrentPassword ? 'text' : 'password'}
                                                    value={currentPassword}
                                                    onChange={(e) => setCurrentPassword(e.target.value)}
                                                    className={`w-full pl-10 pr-12 py-3 border rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors ${isDark
                                                        ? 'bg-gray-600 border-gray-500 text-white placeholder-gray-400'
                                                        : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                                                        }`}
                                                    placeholder="Enter current password"
                                                />
                                                <button
                                                    type="button"
                                                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                                                    className={`absolute right-3 top-1/2 transform -translate-y-1/2 ${isDark ? 'text-gray-400 hover:text-gray-300' : 'text-gray-400 hover:text-gray-600'}`}
                                                >
                                                    {showCurrentPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                                                </button>
                                            </div>
                                        </div>

                                        <div>
                                            <label className={`block text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'} mb-2`}>
                                                New Password
                                            </label>
                                            <div className="relative">
                                                <Lock className={`absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 ${isDark ? 'text-gray-400' : 'text-gray-400'}`} />
                                                <input
                                                    type={showNewPassword ? 'text' : 'password'}
                                                    value={newPassword}
                                                    onChange={(e) => setNewPassword(e.target.value)}
                                                    className={`w-full pl-10 pr-12 py-3 border rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors ${isDark
                                                        ? 'bg-gray-600 border-gray-500 text-white placeholder-gray-400'
                                                        : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                                                        }`}
                                                    placeholder="Enter new password"
                                                />
                                                <button
                                                    type="button"
                                                    onClick={() => setShowNewPassword(!showNewPassword)}
                                                    className={`absolute right-3 top-1/2 transform -translate-y-1/2 ${isDark ? 'text-gray-400 hover:text-gray-300' : 'text-gray-400 hover:text-gray-600'}`}
                                                >
                                                    {showNewPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                                                </button>
                                            </div>

                                            {/* Password Requirements */}
                                            {newPassword && (
                                                <div className={`mt-3 p-4 ${isDark ? 'bg-gray-600' : 'bg-white'} rounded-lg`}>
                                                    <p className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'} mb-2`}>
                                                        Password Requirements:
                                                    </p>
                                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                                        {[
                                                            { key: 'length', text: 'At least 8 characters' },
                                                            { key: 'uppercase', text: 'One uppercase letter' },
                                                            { key: 'lowercase', text: 'One lowercase letter' },
                                                            { key: 'number', text: 'One number' },
                                                            { key: 'special', text: 'One special character' }
                                                        ].map(({ key, text }) => {
                                                            const requirements = validatePassword(newPassword);
                                                            const isValid = requirements[key as keyof typeof requirements];
                                                            return (
                                                                <div key={key} className="flex items-center space-x-2">
                                                                    <div className={`w-4 h-4 rounded-full flex items-center justify-center ${isValid ? 'bg-green-500' : isDark ? 'bg-gray-500' : 'bg-gray-300'
                                                                        }`}>
                                                                        {isValid && <Check className="h-3 w-3 text-white" />}
                                                                    </div>
                                                                    <span className={`text-sm ${isValid
                                                                        ? 'text-green-600 dark:text-green-400'
                                                                        : isDark ? 'text-gray-400' : 'text-gray-600'
                                                                        }`}>
                                                                        {text}
                                                                    </span>
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                            )}
                                        </div>

                                        <div>
                                            <label className={`block text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'} mb-2`}>
                                                Confirm New Password
                                            </label>
                                            <div className="relative">
                                                <Lock className={`absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 ${isDark ? 'text-gray-400' : 'text-gray-400'}`} />
                                                <input
                                                    type={showConfirmPassword ? 'text' : 'password'}
                                                    value={confirmPassword}
                                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                                    className={`w-full pl-10 pr-12 py-3 border rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors ${isDark
                                                        ? 'bg-gray-600 border-gray-500 text-white placeholder-gray-400'
                                                        : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                                                        }`}
                                                    placeholder="Confirm new password"
                                                />
                                                <button
                                                    type="button"
                                                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                                    className={`absolute right-3 top-1/2 transform -translate-y-1/2 ${isDark ? 'text-gray-400 hover:text-gray-300' : 'text-gray-400 hover:text-gray-600'}`}
                                                >
                                                    {showConfirmPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                                                </button>
                                            </div>

                                            {confirmPassword && newPassword !== confirmPassword && (
                                                <p className="mt-2 text-sm text-red-600 dark:text-red-400">
                                                    Passwords do not match
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                <div className="flex justify-end">
                                    <button
                                        onClick={() => handleSave(2)}
                                        disabled={saving || !currentPassword || !newPassword || !confirmPassword || newPassword !== confirmPassword}
                                        className="bg-blue-600 text-white px-8 py-3 rounded-xl font-semibold hover:bg-blue-700 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                                    >
                                        <Save className="h-5 w-5" />
                                        <span>{saving ? 'Updating...' : 'Update Password'}</span>
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* Tab 3: KYC Documents */}
                        {activeTab === 3 && (
                            <div className="space-y-8">
                                <div>
                                    <h3 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'} mb-2`}>
                                        KYC Document Verification
                                    </h3>
                                    <p className={`${isDark ? 'text-gray-300' : 'text-gray-600'} mb-6`}>
                                        Upload your identification documents for account verification
                                    </p>
                                </div>

                                {/* Verification Status */}
                                <div className={`${isDark ? 'bg-gray-700' : 'bg-gray-50'} rounded-2xl p-6`}>
                                    <div className="flex items-center justify-between mb-4">
                                        <h4 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'} flex items-center`}>
                                            <Shield className="h-5 w-5 mr-2 text-green-600" />
                                            Verification Status
                                        </h4>
                                        {getVerificationStatusBadge(profileData.kycVerificationStatus)}
                                    </div>

                                    {profileData.kycSubmittedAt && (
                                        <p className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                                            Submitted on: {new Date(profileData.kycSubmittedAt).toLocaleDateString()}
                                        </p>
                                    )}

                                    {profileData.kycVerifiedAt && (
                                        <p className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                                            Verified on: {new Date(profileData.kycVerifiedAt).toLocaleDateString()}
                                        </p>
                                    )}

                                    {profileData.kycDocumentUrl && (
                                        <div>
                                            <label className={`block text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'} mb-1`}>
                                                Uploaded Document
                                            </label>
                                            <a
                                                href={profileData.kycDocumentUrl}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="text-blue-600 hover:text-blue-700 underline"
                                            >
                                                View Document
                                            </a>
                                        </div>
                                    )}
                                </div>

                                {/* Document Upload */}
                                <div className={`${isDark ? 'bg-gray-700' : 'bg-gray-50'} rounded-2xl p-6`}>
                                    <h4 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'} mb-4 flex items-center`}>
                                        <FileText className="h-5 w-5 mr-2 text-blue-600" />
                                        Document Upload (Government-Issued ID)
                                    </h4>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        <div>
                                            <label className={`block text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'} mb-2`}>
                                                Document Type
                                            </label>
                                            <select
                                                value={profileData.kycDocumentType}
                                                onChange={(e) => handleInputChange('kycDocumentType', e.target.value)}
                                                className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors ${isDark
                                                    ? 'bg-gray-600 border-gray-500 text-white'
                                                    : 'bg-white border-gray-300 text-gray-900'
                                                    }`}
                                            >
                                                {documentTypes.map((type) => (
                                                    <option key={type.value} value={type.value}>{type.label}</option>
                                                ))}
                                            </select>
                                        </div>

                                        {/* <div>
                                            <label className={`block text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'} mb-2`}>
                                                Upload Document
                                            </label>
                                            <div className="flex items-center space-x-4">
                                                <button
                                                    onClick={() => kycFileInputRef.current?.click()}
                                                    className="bg-blue-600 text-white px-4 py-3 rounded-xl font-medium hover:bg-blue-700 transition-colors flex items-center space-x-2"
                                                >
                                                    <Upload className="h-4 w-4" />
                                                    <span>Choose File</span>
                                                </button>
                                                {kycFilePreview && (
                                                    <div className="flex items-center space-x-2">
                                                        <span className={`text-sm ${isDark ? 'text-blue-400' : 'text-blue-600'} flex items-center`}>
                                                            <Check className="h-4 w-4 mr-1" />
                                                            File selected
                                                        </span>
                                                        <button
                                                            onClick={() => clearFilePreview('kyc')}
                                                            className="text-red-600 hover:text-red-700 text-sm font-medium"
                                                        >
                                                            Remove
                                                        </button>
                                                    </div>
                                                )}
                                                {!kycFilePreview && profileData.kycDocumentUrl && (
                                                    <span className={`text-sm ${isDark ? 'text-green-400' : 'text-green-600'} flex items-center`}>
                                                        <Check className="h-4 w-4 mr-1" />
                                                        Document uploaded
                                                    </span>
                                                )}
                                            </div>
                                            {kycFilePreview && (
                                                <div className="mt-2">
                                                    <p className={`text-xs ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>
                                                        Selected: {kycFilePreview.file.name} ({(kycFilePreview.file.size / 1024 / 1024).toFixed(2)} MB)
                                                    </p>
                                                    <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-600'} mt-1`}>
                                                        Preview - save to upload to server
                                                    </p>
                                                </div>
                                            )}
                                            <input
                                                ref={kycFileInputRef}
                                                type="file"
                                                accept="image/*,.pdf"
                                                className="hidden"
                                                onChange={(e) => {
                                                    const file = e.target.files?.[0];
                                                    if (file) handleFileSelect(file, 'kyc');
                                                }}
                                            />
                                        </div> */}

                                        <div>
                                            <label className={`block text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'} mb-2`}>
                                                Upload Document
                                            </label>
                                            <div className="flex flex-wrap items-center gap-4">
                                                <button
                                                    type="button" // Always specify type="button" to prevent form submission
                                                    onClick={() => kycFileInputRef.current?.click()}
                                                    className="bg-blue-600 text-white px-4 py-3 rounded-xl font-medium hover:bg-blue-700 transition-colors flex items-center space-x-2 active:scale-95"
                                                >
                                                    <Upload className="h-4 w-4" />
                                                    <span>Choose File</span>
                                                </button>

                                                {kycFilePreview ? (
                                                    <div className="flex items-center space-x-2 animate-in fade-in duration-300">
                                                        <span className={`text-sm ${isDark ? 'text-blue-400' : 'text-blue-600'} flex items-center font-medium`}>
                                                            <Check className="h-4 w-4 mr-1" />
                                                            Ready to upload
                                                        </span>
                                                        <button
                                                            onClick={() => {
                                                                clearFilePreview('kyc');
                                                                if (kycFileInputRef.current) kycFileInputRef.current.value = ""; // Reset input value
                                                            }}
                                                            className="text-red-600 hover:text-red-700 text-sm font-medium px-2 py-1 hover:bg-red-50 rounded-lg transition-colors"
                                                        >
                                                            Remove
                                                        </button>
                                                    </div>
                                                ) : profileData.kycDocumentUrl && (
                                                    <span className={`text-sm ${isDark ? 'text-green-400' : 'text-green-600'} flex items-center font-medium`}>
                                                        <Check className="h-4 w-4 mr-1" />
                                                        Document uploaded
                                                    </span>
                                                )}
                                            </div>

                                            {kycFilePreview && (
                                                <div className="mt-3 p-3 rounded-lg bg-blue-50/50 border border-blue-100/50">
                                                    <p className={`text-xs font-medium ${isDark ? 'text-blue-300' : 'text-blue-700'}`}>
                                                        Selected: {kycFilePreview.file.name}
                                                    </p>
                                                    <p className={`text-[10px] ${isDark ? 'text-gray-400' : 'text-gray-500'} mt-0.5`}>
                                                        {(kycFilePreview.file.size / (1024 * 1024)).toFixed(2)} MB • Save changes to complete upload
                                                    </p>
                                                </div>
                                            )}

                                            {/* iOS Compatible Input */}
                                            <input
                                                ref={kycFileInputRef}
                                                type="file"
                                                /* sr-only keeps it 'visible' to Safari but hidden from the UI */
                                                className="sr-only"
                                                /* Explicitly allow images and PDFs */
                                                accept="image/jpeg,image/png,image/heic,image/heif,application/pdf"
                                                onChange={(e) => {
                                                    const file = e.target.files?.[0];
                                                    if (file) handleFileSelect(file, 'kyc');
                                                }}
                                            />
                                        </div>

                                    </div>

                                    {profileData.kycDocumentUrl && (
                                        <div className="mt-4">
                                            <p className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'} mb-2`}>
                                                Current Document:
                                            </p>
                                            <a
                                                href={profileData.kycDocumentUrl}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="text-blue-600 hover:text-blue-700 text-sm underline"
                                            >
                                                View uploaded document
                                            </a>
                                        </div>
                                    )}
                                </div>

                                <div className="flex justify-end">
                                    <button
                                        onClick={() => handleSave(3)}
                                        disabled={saving}
                                        className="bg-blue-600 text-white px-8 py-3 rounded-xl font-semibold hover:bg-blue-700 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                                    >
                                        <Save className="h-5 w-5" />
                                        <span>{saving ? 'Saving...' : 'Save Changes'}</span>
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </main>
        </div>
    );
}
